import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client.safe';
import { useDevMode } from '@/hooks/useDevMode';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { UserPlus, Users, Copy, Check, Eye, EyeOff, Trash2, RefreshCw } from 'lucide-react';

interface Tester {
  user_id: string;
  full_name: string | null;
  email: string | null;
  organization_id: string | null;
  organization_name: string | null;
  is_currently_active: boolean;
  total_sessions: number;
  total_minutes: number;
  total_revisions: number;
  last_active_at: string | null;
}

export default function AdminTesterManagementPage() {
  const navigate = useNavigate();
  const { effectiveRole, isSimulating, loading: roleLoading } = useDevMode();
  const { toast } = useToast();
  
  const [testers, setTesters] = useState<Tester[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [testerName, setTesterName] = useState('');
  const [testerEmail, setTesterEmail] = useState('');
  const [orgName, setOrgName] = useState('');
  
  // Result state
  const [createdCredentials, setCreatedCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check access
  useEffect(() => {
    if (roleLoading) return;
    if (effectiveRole !== 'system_admin') {
      navigate('/');
    }
  }, [effectiveRole, roleLoading, navigate]);

  // Fetch testers
  const fetchTesters = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tester_activity_summary')
        .select('*')
        .order('last_active_at', { ascending: false, nullsFirst: false });

      if (error) throw error;
      setTesters(data || []);
    } catch (error) {
      console.error('Error fetching testers:', error);
      toast({
        title: 'Fel',
        description: 'Kunde inte hämta testare',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTesters();
  }, []);

  // Auto-generate org name when tester name changes
  useEffect(() => {
    if (testerName && !orgName) {
      setOrgName(`Testare ${testerName} AB`);
    }
  }, [testerName, orgName]);

  const handleCreateTester = async () => {
    if (!testerName || !testerEmail || !orgName) {
      toast({
        title: 'Fyll i alla fält',
        description: 'Namn, e-post och organisation krävs',
        variant: 'destructive'
      });
      return;
    }

    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke('register-tester', {
        body: {
          tester_name: testerName,
          tester_email: testerEmail,
          org_name: orgName
        }
      });

      if (error) throw error;

      if (data?.success) {
        setCreatedCredentials({
          email: data.email,
          password: data.temp_password
        });
        
        toast({
          title: 'Testare skapad!',
          description: `${testerName} har registrerats med organisation "${orgName}"`
        });

        // Refresh the list
        await fetchTesters();
      } else {
        throw new Error(data?.error || 'Okänt fel');
      }
    } catch (error: any) {
      console.error('Error creating tester:', error);
      toast({
        title: 'Fel vid skapande',
        description: error.message || 'Kunde inte skapa testare',
        variant: 'destructive'
      });
    } finally {
      setCreating(false);
    }
  };

  const copyCredentials = () => {
    if (!createdCredentials) return;
    
    const text = `E-post: ${createdCredentials.email}\nLösenord: ${createdCredentials.password}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetForm = () => {
    setTesterName('');
    setTesterEmail('');
    setOrgName('');
    setCreatedCredentials(null);
    setShowPassword(false);
    setCopied(false);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just nu';
    if (diffHours < 24) return `${diffHours}h sedan`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d sedan`;
    return date.toLocaleDateString('sv-SE');
  };

  if (roleLoading || effectiveRole !== 'system_admin') {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hantera testare</h1>
          <p className="text-gray-600">Registrera och hantera studenter som testar systemet</p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchTesters} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Uppdatera
          </Button>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}>
                <UserPlus className="h-4 w-4 mr-2" />
                Ny testare
              </Button>
            </DialogTrigger>
            
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Registrera ny testare</DialogTitle>
                <DialogDescription>
                  Skapa ett konto för en student som ska testa systemet. De får en egen organisation.
                </DialogDescription>
              </DialogHeader>
              
              {!createdCredentials ? (
                <>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Namn</Label>
                      <Input
                        id="name"
                        placeholder="Kalle Testsson"
                        value={testerName}
                        onChange={(e) => setTesterName(e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">E-postadress</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="kalle@student.se"
                        value={testerEmail}
                        onChange={(e) => setTesterEmail(e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="org">Organisationsnamn</Label>
                      <Input
                        id="org"
                        placeholder="Testare Kalle AB"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                      />
                      <p className="text-xs text-gray-500">
                        Detta blir testarens "revisionsbolag" i systemet
                      </p>
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={closeDialog}>
                      Avbryt
                    </Button>
                    <Button onClick={handleCreateTester} disabled={creating}>
                      {creating ? 'Skapar...' : 'Skapa testare'}
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <>
                  <div className="py-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                      <p className="font-medium text-green-800">
                        ✅ Testaren har skapats!
                      </p>
                      
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 w-20">E-post:</span>
                          <code className="bg-white px-2 py-1 rounded text-sm flex-1">
                            {createdCredentials.email}
                          </code>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 w-20">Lösenord:</span>
                          <code className="bg-white px-2 py-1 rounded text-sm flex-1">
                            {showPassword ? createdCredentials.password : '••••••••••••'}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                      
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={copyCredentials}
                      >
                        {copied ? (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Kopierat!
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-2" />
                            Kopiera inloggningsuppgifter
                          </>
                        )}
                      </Button>
                    </div>
                    
                    <p className="text-sm text-gray-500 mt-3">
                      Skicka inloggningsuppgifterna till testaren. De kan logga in direkt.
                    </p>
                  </div>
                  
                  <DialogFooter>
                    <Button onClick={closeDialog}>Stäng</Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Totalt antal testare</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{testers.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Aktiva just nu</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {testers.filter(t => t.is_currently_active).length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Totalt körda analyser</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {testers.reduce((sum, t) => sum + (t.total_revisions || 0), 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Testers table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Registrerade testare
          </CardTitle>
          <CardDescription>
            Alla studenter som är registrerade för att testa systemet
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Laddar...</div>
          ) : testers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Inga testare registrerade ännu. Klicka "Ny testare" för att komma igång.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Namn</TableHead>
                  <TableHead>Organisation</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Sessioner</TableHead>
                  <TableHead className="text-right">Total tid</TableHead>
                  <TableHead className="text-right">Analyser</TableHead>
                  <TableHead className="text-right">Senast aktiv</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {testers.map((tester) => (
                  <TableRow key={tester.user_id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{tester.full_name || '-'}</div>
                        <div className="text-sm text-gray-500">{tester.email}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {tester.organization_name || '-'}
                    </TableCell>
                    <TableCell>
                      {tester.is_currently_active ? (
                        <Badge className="bg-green-100 text-green-800">Online</Badge>
                      ) : (
                        <Badge variant="secondary">Offline</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{tester.total_sessions}</TableCell>
                    <TableCell className="text-right">
                      {formatDuration(tester.total_minutes)}
                    </TableCell>
                    <TableCell className="text-right">{tester.total_revisions}</TableCell>
                    <TableCell className="text-right text-gray-500">
                      {formatDate(tester.last_active_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
